import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { getCalendarEvents } from '@/features/calendar/services/calendarApi'
import { getOutlookCalendarEvents } from '@/features/outlook-calendar/services/outlookCalendarApi'
import type { BookingDurationMinutes, CalendarChoice } from '../types/index.js'

type Props = {
  value: Date | null
  onChange: (date: Date) => void
  durationMinutes: BookingDurationMinutes
  workspaceId: string
  calendarChoice: CalendarChoice | null
  googleConnected: boolean
  outlookConnected: boolean
}

// Half-hour slots covering the designed 9 AM – 6 PM working-hours window.
const SLOT_START_MINUTES = 9 * 60
const SLOT_END_MINUTES = 18 * 60
const SLOT_STEP_MINUTES = 30

function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + days)
  return out
}

function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1)
}

function localDateKey(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatMonthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

export function formatValueLabel(d: Date): string {
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}

// Picker button shows the booked range, not just the start — duration is
// picked in a separate control but the two are one decision from the rep's
// point of view, so the field reflects both.
export function formatRangeLabel(d: Date, durationMinutes: number): string {
  const end = new Date(d.getTime() + durationMinutes * 60 * 1000)
  const datePart = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  // Forced 12-hour — the field is half-width next to the duration chips, and
  // a locale-default 24-hour range ("14:30 – 15:00") was wide enough to wrap
  // the button onto two lines.
  const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true }
  const startTimeFull = d.toLocaleTimeString(undefined, timeOpts)
  const endTime = end.toLocaleTimeString(undefined, timeOpts)
  // Drop the start's AM/PM only when it matches the end's — e.g. "9:00–9:30
  // AM", but keep both for a range crossing noon, e.g. "11:45 AM–12:15 PM".
  const samePeriod = /am$/i.test(startTimeFull) === /am$/i.test(endTime)
  const startTime = samePeriod ? startTimeFull.replace(/\s?[AP]M$/i, '') : startTimeFull
  return `${datePart}, ${startTime}–${endTime}`
}

function formatSlotLabel(minutes: number): string {
  const hour24 = Math.floor(minutes / 60)
  const minute = minutes % 60
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`
}

type BusyEvent = { start: number; end: number }

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const BookingDateTimePicker: React.FC<Props> = ({ value, onChange, durationMinutes, workspaceId, calendarChoice, googleConnected, outlookConnected }) => {
  const { currentTheme: t } = useTheme()
  const [open, setOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(value ?? new Date()))
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(value ?? new Date()))
  const [events, setEvents] = useState<BusyEvent[]>([])
  const [dayHasEventKeys, setDayHasEventKeys] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Which provider(s) to pull busy data from mirrors CalendarView's merge
  // pattern: a specific choice only checks that provider, 'both'/unset
  // unions whichever providers are actually connected.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const from = startOfMonth(viewMonth).toISOString()
    const to = addMonths(viewMonth, 1).toISOString()
    const wantGoogle = (calendarChoice === 'google' || calendarChoice === 'both' || !calendarChoice) && googleConnected
    const wantOutlook = (calendarChoice === 'outlook' || calendarChoice === 'both' || !calendarChoice) && outlookConnected

    Promise.all([
      wantGoogle ? getCalendarEvents(workspaceId, from, to).catch(() => []) : Promise.resolve([]),
      wantOutlook ? getOutlookCalendarEvents(workspaceId, from, to).catch(() => []) : Promise.resolve([]),
    ]).then(([google, outlook]) => {
      if (cancelled) return
      const all = [...google, ...outlook]
      const busy: BusyEvent[] = []
      const dayKeys = new Set<string>()
      for (const e of all) {
        if (e.isAllDay) continue
        const start = new Date(e.start)
        const end = new Date(e.end)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue
        dayKeys.add(localDateKey(start))
        if (e.busy) busy.push({ start: start.getTime(), end: end.getTime() })
      }
      setEvents(busy)
      setDayHasEventKeys(dayKeys)
    })
    return () => { cancelled = true }
  }, [open, viewMonth, workspaceId, calendarChoice, googleConnected, outlookConnected])

  const slots = useMemo(() => {
    const out: number[] = []
    for (let m = SLOT_START_MINUTES; m < SLOT_END_MINUTES; m += SLOT_STEP_MINUTES) out.push(m)
    return out
  }, [])

  const isSlotBusy = useCallback((minutes: number): boolean => {
    const slotStart = new Date(selectedDay)
    slotStart.setHours(0, minutes, 0, 0)
    const slotEnd = new Date(slotStart.getTime() + SLOT_STEP_MINUTES * 60 * 1000)
    return events.some((e) => e.start < slotEnd.getTime() && e.end > slotStart.getTime())
  }, [events, selectedDay])

  // Only matters for today — any slot on a future day is never "past". Not
  // memoized against a ticking clock: it's re-evaluated per render, which is
  // plenty fresh for a picker that's only open for a few seconds at a time.
  const isSlotPast = useCallback((minutes: number): boolean => {
    const slotStart = new Date(selectedDay)
    slotStart.setHours(0, minutes, 0, 0)
    return slotStart.getTime() < Date.now()
  }, [selectedDay])

  const selectedMinutes = useMemo(() => {
    if (!value || localDateKey(value) !== localDateKey(selectedDay)) return null
    return value.getHours() * 60 + value.getMinutes()
  }, [value, selectedDay])

  const today = useMemo(() => startOfDay(new Date()), [])

  const weeks = useMemo(() => {
    const first = startOfMonth(viewMonth)
    const gridStart = addDays(first, -first.getDay())
    const out: Date[][] = []
    for (let w = 0; w < 6; w++) {
      const week: Date[] = []
      for (let d = 0; d < 7; d++) week.push(addDays(gridStart, w * 7 + d))
      out.push(week)
    }
    return out
  }, [viewMonth])

  const handlePickSlot = useCallback((minutes: number) => {
    const next = new Date(selectedDay)
    next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
    onChange(next)
    setOpen(false)
  }, [selectedDay, onChange])

  return (
    <div className="flex flex-col gap-1.5 relative" ref={containerRef}>
      <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Date & time</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-11 w-full min-w-0 flex items-center gap-2 rounded-[9px] px-[10px]"
        style={{ border: `1px solid ${t.border}`, backgroundColor: t.inputBackground }}
      >
        <CalendarClock size={14} className="shrink-0" style={{ color: t.accent }} />
        {value ? (
          <span className="text-[12.5px] font-semibold truncate" style={{ color: t.text }}>{formatRangeLabel(value, durationMinutes)}</span>
        ) : (
          <span className="text-[12.5px]" style={{ color: t.mutedText }}>Pick a date & time</span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-1.5 flex"
          style={{
            height: 346,
            backgroundColor: t.sectionBackground,
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            boxShadow: t.cardShadow,
          }}
        >
          {/* Left column — month calendar */}
          <div className="flex flex-col gap-3.5" style={{ width: 300, padding: 18 }}>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewMonth((m) => addMonths(m, -1))}
                className="flex items-center justify-center"
                style={{ width: 26, height: 26, backgroundColor: t.hoverBackground, borderRadius: 7 }}
              >
                <ChevronLeft size={14} style={{ color: t.mutedText }} />
              </button>
              <span className="text-[13px] font-bold" style={{ color: t.text }}>{formatMonthLabel(viewMonth)}</span>
              <button
                type="button"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                className="flex items-center justify-center"
                style={{ width: 26, height: 26, backgroundColor: t.hoverBackground, borderRadius: 7 }}
              >
                <ChevronRight size={14} style={{ color: t.mutedText }} />
              </button>
            </div>

            <div className="grid grid-cols-7">
              {WEEKDAY_LABELS.map((w, i) => (
                <div key={i} className="flex items-center justify-center" style={{ width: 36, height: 20 }}>
                  <span className="text-[11px] font-bold" style={{ color: t.mutedText }}>{w}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-0.5">
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7">
                  {week.map((day) => {
                    const inMonth = day.getMonth() === viewMonth.getMonth()
                    const key = localDateKey(day)
                    const isSelected = key === localDateKey(selectedDay)
                    const hasEvents = dayHasEventKeys.has(key)
                    const isPast = day.getTime() < today.getTime()
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={isPast}
                        onClick={() => setSelectedDay(startOfDay(day))}
                        className="flex flex-col items-center justify-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 18,
                          backgroundColor: isSelected ? t.accent : 'transparent',
                          cursor: isPast ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <span
                          className="text-[12px]"
                          style={{
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? '#fff' : inMonth ? t.text : t.mutedText,
                            opacity: isPast ? 0.35 : inMonth ? 1 : 0.5,
                          }}
                        >
                          {day.getDate()}
                        </span>
                        {hasEvents && (
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: 2,
                              marginTop: 1,
                              backgroundColor: isSelected ? '#fff' : t.accent,
                            }}
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, backgroundColor: t.border }} />

          {/* Right column — time slots */}
          <div className="flex flex-col" style={{ width: 190, overflow: 'hidden' }}>
            <div style={{ padding: '18px 16px 10px 16px' }}>
              <span className="text-[13px] font-bold" style={{ color: t.text }}>{formatDayLabel(selectedDay)}</span>
            </div>
            <div className="flex flex-col gap-1 overflow-y-auto custom-scrollbar" style={{ padding: '0 16px 16px 16px' }}>
              {slots.map((minutes) => {
                const busy = isSlotBusy(minutes)
                const past = isSlotPast(minutes)
                const disabled = busy || past
                const selected = selectedMinutes === minutes
                return (
                  <button
                    key={minutes}
                    type="button"
                    disabled={disabled}
                    onClick={() => handlePickSlot(minutes)}
                    className="flex items-center px-2.5 shrink-0"
                    style={{
                      height: 32,
                      borderRadius: 8,
                      backgroundColor: selected ? t.accent : disabled ? 'transparent' : t.hoverBackground,
                      border: disabled ? `1px solid ${t.border}` : 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: past && !busy ? 0.5 : 1,
                    }}
                  >
                    <span
                      className="text-[11.5px]"
                      style={{
                        fontWeight: selected ? 700 : 500,
                        color: selected ? '#fff' : disabled ? t.mutedText : t.text,
                      }}
                    >
                      {formatSlotLabel(minutes)}{busy ? ' · Busy' : ''}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(BookingDateTimePicker)
